import React, { useState } from 'react';
import { Alert, Button, Form, Input, Typography } from 'antd';
import UserStore from '../../../stores/UserStore';
import { useHistory, useLocation } from 'react-router-dom';

const { Paragraph } = Typography;

interface IProps {
    email?: string;
}

interface IResetFormValues {
    email: string;
}

type FormState = 'idle' | 'success' | 'email_not_found' | 'rate_limited';

const ResetPassword: React.FC<IProps> = ({ email }) => {
    const [formState, setFormState] = useState<FormState>('idle');
    const [loading, setLoading] = useState(false);
    const [submittedEmail, setSubmittedEmail] = useState('');

    const { search } = useLocation();
    const history = useHistory();

    const sendPasswordReset = async (values: IResetFormValues) => {
        try {
            setLoading(true);
            setSubmittedEmail(values.email);

            await UserStore.resetPassword(values.email);

            setFormState('success');
        } catch (error: any) {
            const status = error?.response?.status;
            const code = error?.response?.data?.code;

            if (status === 404 && code === 'email_not_found') {
                setFormState('email_not_found');
            } else if (status === 429) {
                setFormState('rate_limited');
            } else {
                setFormState('idle');
                // surface unexpected errors via the existing generic handler in api.ts
            }
        } finally {
            setLoading(false);
        }
    };

    const goTo = (nextPathname: string) => {
        history.push({ pathname: nextPathname, search });
    };

    if (formState === 'success') {
        return (
            <div>
                <Paragraph>
                    If an account is associated with the entered email, you will receive an email with instructions to
                    reset your password. Please check your inbox and spam folder.
                </Paragraph>

                <Button type="primary" block onClick={() => goTo('/login/')}>
                    Back to Login
                </Button>
            </div>
        );
    }

    if (formState === 'email_not_found') {
        return (
            <div>
                <Alert
                    type="warning"
                    showIcon
                    message="No account found"
                    description={
                        <>
                            We couldn&apos;t find an account associated with <strong>{submittedEmail}</strong>. You may
                            need to create an account first.
                        </>
                    }
                    className="mb-3"
                />

                <div className="d-flex flex-column" style={{ gap: 8 }}>
                    <Button type="primary" block onClick={() => goTo('/register/')}>
                        Create an Account
                    </Button>

                    <Button block onClick={() => goTo('/login/')}>
                        Back to Login
                    </Button>
                </div>
            </div>
        );
    }

    if (formState === 'rate_limited') {
        return (
            <div>
                <Alert
                    type="error"
                    showIcon
                    message="Too many attempts"
                    description="You have made too many password reset requests. Please wait a few minutes before trying again."
                    className="mb-3"
                />

                <Button block onClick={() => goTo('/login/')}>
                    Back to Login
                </Button>
            </div>
        );
    }

    return (
        <Form<IResetFormValues> onFinish={sendPasswordReset} layout="vertical">
            <Paragraph>To reset your password, please enter the email address associated with your account.</Paragraph>

            <Form.Item
                name="email"
                initialValue={email}
                label="Email Address"
                rules={[
                    {
                        type: 'email',
                        required: true,
                        message: 'Please enter a valid email address',
                    },
                ]}
                hasFeedback
            >
                <Input autoComplete="email" />
            </Form.Item>

            <div className="d-flex flex-column">
                <Button type="primary" htmlType="submit" block loading={loading} className="mb-2">
                    Request Password Reset Link
                </Button>

                <Button onClick={() => goTo('/login/')}>Back to Login</Button>
            </div>
        </Form>
    );
};

export default ResetPassword;
